import {
  SIGN_IN_PATH,
  type AssessmentId,
  type ConfigurationScopeId,
  type FullFilter,
  type OrganisationId,
  type PersonId,
  type SummaryGrouping,
  type UserId,
} from "@thalamos/haven-common";

import type { AdminOrganisationTab } from "../pages/Admin/adminOrganisationTabs.js";
import type { HistoryItems } from "../pages/FullHistory/fullHistory.js";
import type { PersonTab } from "../pages/Persons/personTabs.js";

/** The query parameter the organisation configuration list is narrowed to one configuration by. */
export const ORGANISATION_CONFIGURATION_SCOPE_PARAMETER = "configuration";

/** The query parameter a summary's grouping is named by, absent for the default. */
export const SUMMARY_GROUPING_PARAMETER = "group";

/** The query parameters naming how an assessment's full view is laid out. */
export const FULL_VIEW_PARAMETERS = {
  filter: "show",
  fullText: "text",
  open: "open",
} as const;

/** How an assessment's full view is laid out; each part left out is its default. */
export type FullView = {
  /** All items by default. */
  filter?: FullFilter;
  /** Long text clamped by default. */
  fullText?: boolean;
  /** The groups of the domains open; every domain is closed by default. */
  open?: readonly string[];
};

/** The query parameters naming what a young person's full history shows. */
export const HISTORY_PARAMETERS = {
  family: "instrument",
  from: "from",
  to: "to",
  items: "items",
  window: "window",
  back: "back",
  item: "item",
} as const;

/** What a young person's full history shows; each part left out is its default. */
export type HistoryView = {
  /** The family of instruments; the most recently assessed by default. */
  family?: string | undefined;
  /** The first day of the range, as `YYYY-MM-DD`; a year before its end by default. */
  from?: string | undefined;
  /** The last day of the range, included; open by default. */
  to?: string | undefined;
  /** What calls for action now by default. */
  items?: HistoryItems;
  /** The day the chart's and table's columns end on; the latest in the range by default. */
  window?: string | undefined;
  /** The assessment the history was opened from, to go back to. */
  back?: AssessmentId | undefined;
  /** The key of an item to pick out. */
  item?: string | undefined;
};

const withQuery = (path: string, query: URLSearchParams) => {
  const search = query.toString();

  return search === "" ? path : `${path}?${search}`;
};

/**
 * Path builders. Kept free of component imports so anything may depend on them
 * — the route tree in `createRouter` imports pages, so importing that instead
 * would drag the whole page graph into every caller.
 */
export const createRoute = {
  home: () => "/",
  signIn: (returnTo?: string) =>
    returnTo
      ? `${SIGN_IN_PATH}?returnTo=${encodeURIComponent(returnTo)}`
      : SIGN_IN_PATH,
  sessionContextSelector: (returnTo?: string) =>
    returnTo
      ? `/session-context-selector?returnTo=${encodeURIComponent(returnTo)}`
      : "/session-context-selector",
  profile: () => "/profile",
  persons: () => "/persons",
  newPerson: () => "/persons/new",
  matchPerson: () => "/persons/match",
  person: (personId: PersonId) => `/persons/${personId}`,
  personTab: (personId: PersonId, tab: PersonTab) =>
    `/persons/${personId}/${tab}`,
  approvals: () => "/approvals",
  users: () => "/users",
  user: (userId: UserId) => `/users/${userId}`,
  assessment: (assessmentId: AssessmentId) => `/assessments/${assessmentId}`,
  assessmentDomain: (assessmentId: AssessmentId, group: string) =>
    `/assessments/${assessmentId}/domains/${encodeURIComponent(group)}`,
  assessmentItem: (assessmentId: AssessmentId, group: string, item: string) =>
    `/assessments/${assessmentId}/domains/${encodeURIComponent(group)}/${encodeURIComponent(item)}`,
  /**
   * An assessment's summary, compared with another where one is named, and
   * grouped by domain where asked; grouping by rating is the default and so
   * leaves the query off.
   */
  assessmentSummary: (
    assessmentId: AssessmentId,
    {
      comparedWith,
      grouping = "rating",
    }: {
      comparedWith?: AssessmentId | undefined;
      grouping?: SummaryGrouping;
    } = {},
  ) =>
    `/assessments/${assessmentId}/summary${
      comparedWith === undefined ? "" : `/compared-with/${comparedWith}`
    }${grouping === "domain" ? `?${SUMMARY_GROUPING_PARAMETER}=domain` : ""}`,
  /** An assessment's every answer, laid out as the `FullView` names. */
  assessmentFull: (
    assessmentId: AssessmentId,
    { filter = "all", fullText = false, open = [] }: FullView = {},
  ) => {
    const query = new URLSearchParams();

    if (filter !== "all") {
      query.set(FULL_VIEW_PARAMETERS.filter, filter);
    }

    if (fullText) {
      query.set(FULL_VIEW_PARAMETERS.fullText, "full");
    }

    for (const group of open) {
      query.append(FULL_VIEW_PARAMETERS.open, group);
    }

    return withQuery(`/assessments/${assessmentId}/full`, query);
  },
  /** How a young person's approved assessments have gone, shown as the `HistoryView` names. */
  fullHistory: (
    personId: PersonId,
    { items = "current", ...view }: HistoryView = {},
  ) => {
    const query = new URLSearchParams();
    const parts: [keyof typeof HISTORY_PARAMETERS, string | undefined][] = [
      ["family", view.family],
      ["from", view.from],
      ["to", view.to],
      ["items", items === "current" ? undefined : items],
      ["window", view.window],
      ["back", view.back],
      ["item", view.item],
    ];

    for (const [part, value] of parts) {
      if (value !== undefined) {
        query.set(HISTORY_PARAMETERS[part], value);
      }
    }

    return withQuery(`/persons/${personId}/full-history`, query);
  },
  admin: () => "/admin",
  adminUsers: () => "/admin/users",
  adminUser: (userId: UserId) => `/admin/users/${userId}`,
  adminOrganisations: () => "/admin/organisations",
  adminOrganisation: (
    organisationId: OrganisationId,
    tab?: AdminOrganisationTab,
  ) =>
    tab === undefined
      ? `/admin/organisations/${organisationId}`
      : `/admin/organisations/${organisationId}/${tab}`,
  adminConfigurationScopes: () => "/admin/configuration/chain",
  adminConfigurationScope: (scopeId: ConfigurationScopeId) =>
    `/admin/configuration/chain/${scopeId}`,
  adminOrganisationConfiguration: (
    configurationScopeId?: ConfigurationScopeId,
  ) =>
    configurationScopeId === undefined
      ? "/admin/configuration/organisations"
      : `/admin/configuration/organisations?${ORGANISATION_CONFIGURATION_SCOPE_PARAMETER}=${configurationScopeId}`,
  adminAuditTrail: () => "/admin/audit-trail",
  adminPersons: () => "/admin/persons",
};
